import { useCallback, useMemo } from 'react';
import { useTerminalExtStore } from '../../store/terminalExtStore';
import { currentPlatform, matchPaneChord } from './terminalPaneShortcuts';
import { countLeaves } from './paneTreeModel';

/** Chords that only mean something with more than one pane. In a lone pane
 * they belong to the shell / agent (Alt+←/→ is word-jump in bash, zsh and
 * most TUIs), so a single-pane tab behaves exactly as before splits. */
const NEEDS_PANES = new Set(['focus', 'resize', 'cycle', 'zoom']);

/** The pane action this key means in `tabId` right now, or null. */
function chordFor(e, platform, tabId) {
  const action = matchPaneChord(e, platform);
  if (!action || !NEEDS_PANES.has(action.type)) return action;
  const tab = useTerminalExtStore.getState().tabs.find((t) => t.id === tabId);
  return tab && countLeaves(tab.layout) > 1 ? action : null;
}

/** Editable elements that are NOT an xterm's own input keep their keys. */
function isForeignEditable(target) {
  if (!target || typeof target.closest !== 'function') return false;
  if (target.classList?.contains('xterm-helper-textarea')) return false;
  return !!target.closest('input, textarea, select, [contenteditable="true"]');
}

/**
 * Binds the pane chord table to the store for one tab. Returns
 *  - `onKeyDownCapture` for the grid root: runs the action and stops the
 *    event before it reaches the xterm (or a global handler such as
 *    Duplicate's ⌘D on macOS);
 *  - `keyFilter` for each xterm's custom key handler: `false` for exactly
 *    these chords, so the shell or agent never receives them either.
 */
export function useTerminalPaneShortcuts(tabId) {
  const platform = useMemo(() => currentPlatform(), []);

  const run = useCallback(
    (action) => {
      const st = useTerminalExtStore.getState();
      const tab = st.tabs.find((t) => t.id === tabId);
      if (!tab) return;
      const paneId = tab.focusedPaneId;
      switch (action.type) {
        case 'split':
          st.splitPane(paneId, action.dir);
          break;
        case 'focus':
          st.focusNeighbour(tabId, action.direction);
          break;
        case 'resize':
          st.resizeFocused(tabId, action.direction);
          break;
        case 'cycle':
          st.cycleFocus(tabId, action.delta);
          break;
        case 'zoom':
          st.toggleZoom(tabId);
          break;
        case 'close':
          st.closePane(paneId);
          break;
        default:
      }
    },
    [tabId]
  );

  const onKeyDownCapture = useCallback(
    (e) => {
      if (isForeignEditable(e.target)) return;
      const action = chordFor(e, platform, tabId);
      if (!action) return;
      e.preventDefault();
      e.stopPropagation();
      run(action);
    },
    [platform, run, tabId]
  );

  const keyFilter = useCallback((e) => !chordFor(e, platform, tabId), [platform, tabId]);

  return { onKeyDownCapture, keyFilter };
}
